import React, { useState } from 'react';
import { Lightbulb, Loader2, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { WorkflowDefinition } from '../../types/workflows';
import { apiClient } from '../../lib/api';

interface WorkflowGenerationFormProps {
  datasetId: string;
  datasetName: string;
  onWorkflowGenerated: (workflow: WorkflowDefinition) => void;
  onCancel: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const EXAMPLE_PROMPTS = [
  "Send personalized outreach emails to leads and follow up with non-responders after 3 days",
  "Filter high-scoring leads, enrich with LinkedIn data, then send targeted messages based on company size",
  "Automatically send welcome emails to new leads and classify replies as interested or not interested",
  "Find leads in technology industry, verify email addresses, then send cold outreach sequence",
  "Weekly digest email to all leads with recent company news and follow-up opportunities"
];

export const WorkflowGenerationForm: React.FC<WorkflowGenerationFormProps> = ({
  datasetId,
  datasetName,
  onWorkflowGenerated,
  onCancel,
  isLoading,
  setIsLoading
}) => {
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const VALIDATION_RULES = {
    empty: {
      check: (desc: string) => !desc.trim(),
      message: 'Please describe what you want your workflow to do'
    },
    tooShort: {
      check: (desc: string) => desc.trim().length < 10,
      message: 'Please provide a more detailed description to help us create the perfect workflow'
    },
    tooLong: {
      check: (desc: string) => desc.trim().length > 1000,
      message: 'Please keep your description under 1000 characters for best results'
    }
  };

  const validateDescription = (desc: string): string | null => {
    for (const rule of Object.values(VALIDATION_RULES)) {
      if (rule.check(desc)) {
        return rule.message;
      }
    }
    return null;
  };

  const ERROR_MAPPINGS = {
    status: {
      400: 'Please refine your description and try again',
      401: 'Your session has expired. Please refresh the page and try again',
      403: 'You may not have permission to create workflows. Please contact support',
      429: 'You\'re creating workflows quickly! Please wait a moment before trying again',
      500: 'Something went wrong on our end. Please try again in a few moments'
    },
    codes: {
      NETWORK_ERROR: 'Unable to connect. Please check your internet connection and try again',
      ECONNABORTED: 'This is taking longer than expected. Please try again or simplify your description'
    }
  };

  const getStatusMessage = (status: number): string => {
    return ERROR_MAPPINGS.status[status as keyof typeof ERROR_MAPPINGS.status] || 
           (status >= 500 ? ERROR_MAPPINGS.status[500] : '');
  };

  const getApiDetailMessage = (error: any): string => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.error) return detail.error;
    return '';
  };

  const getDetailedErrorMessage = (error: any): string => {
    const statusMessage = error.response?.status ? getStatusMessage(error.response.status) : '';
    if (statusMessage) return statusMessage;

    const codeMessage = error.code ? ERROR_MAPPINGS.codes[error.code as keyof typeof ERROR_MAPPINGS.codes] : '';
    if (codeMessage) return codeMessage;

    if (error.message?.includes('timeout')) return ERROR_MAPPINGS.codes.ECONNABORTED;
    if (!error.response) return ERROR_MAPPINGS.codes.NETWORK_ERROR;

    const apiMessage = getApiDetailMessage(error);
    if (apiMessage) return apiMessage;

    return 'Unable to create your workflow. Please try rephrasing your description';
  };

  const createWorkflowData = (response: any): WorkflowDefinition => {
    return {
      ...response.workflow_definition,
      description: response.description || description.trim(),
      status: 'DRAFT' as const
    };
  };

  const handleGenerate = async () => {
    const validationError = validateDescription(description);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.generateWorkflowFromDescription(
        description.trim(),
        datasetId,
        'openai'
      );
      const workflowData = createWorkflowData(response);
      onWorkflowGenerated(workflowData);
    } catch (err: any) {
      console.error('Error generating workflow:', err);
      setError(getDetailedErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setDescription(example);
    setError(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div className='max-h-[60vh] overflow-y-auto space-y-6 pr-2'>
        {/* Introduction */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-3">
            <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-900">AI-Powered Workflow Generation</h3>
              <p className="text-sm text-blue-700 mt-1">
                Describe your automation goals in plain English. Our AI will create a complete workflow 
                with the right activities, conditions, and timing for your <strong>{datasetName}</strong> dataset.
              </p>
            </div>
          </div>
        </div>

        {/* Description Input */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label htmlFor="workflow-description" className="block text-sm font-medium text-gray-700">
              What would you like your workflow to do?
            </label>
            <span className={`text-xs ${description.length > 1000 ? 'text-red-500' : description.length > 800 ? 'text-yellow-500' : 'text-gray-400'}`}>
              {description.length}/1000
            </span>
          </div>
          <textarea
            id="workflow-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Describe your automation goals... (e.g., Send personalized emails to leads and follow up with non-responders)"
            className={`w-[99%] ml-1 h-32 px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
              error 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            disabled={isLoading}
            maxLength={1000}
          />
          {error && (
            <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {description.length > 0 && description.length < 10 && !error && (
            <div className="text-xs text-gray-500">
              💡 Try to be more specific about what you want your workflow to accomplish
            </div>
          )}
        </div>

        {/* Example Prompts */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">Example automations:</span>
          </div>
          <div className="grid gap-2">
            {EXAMPLE_PROMPTS.map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleClick(example)}
                disabled={isLoading}
                className="text-left p-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <Button 
          variant="ghost" 
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        
        <Button 
          onClick={handleGenerate}
          disabled={isLoading || !description.trim()}
          className="min-w-[140px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Generating...
            </>
          ) : (
            <>
              Generate Workflow
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};